import { useState } from 'react'
import { Loader2, LogOut } from 'lucide-react'
import { useTranslation } from 'react-i18next'

const LogoutConfirmationModal = ({ isOpen, onClose, onConfirm, userType = 'PET_GUARDIAN' }) => {
  const [isLoggingOut, setIsLoggingOut] = useState(false)
  const { t, i18n } = useTranslation()
  const modalDirection = typeof i18n?.dir === 'function' ? i18n.dir() : 'ltr'
  const isRTL = modalDirection === 'rtl'

  const title = t('modals.logout.title', 'Confirm Logout')
  const question = t('modals.logout.question', 'Are you sure you want to log out?')
  const subtitle = t('modals.logout.subtitle', "You'll need to sign in again to access your account.")
  const confirmText = t('modals.logout.confirm', 'Logout')
  const cancelText = t('common.cancel', 'Cancel')
  const loggingOutText = t('modals.logout.loggingOut', 'Logging out...')

  // Color schemes based on user type
  const getColorScheme = (userType) => {
    switch (userType) {
      case "PET_GUARDIAN":
        return {
          primary: "#8548A8",
          hover: "#7A4397",
        }
      case "VETERINARIAN":
        return {
          primary: "#00D4B3",
          hover: "#00C4A7",
        }
      case "SERVICE_PROVIDER":
        return {
          primary: "#D65745",
          hover: "#C7493A",
        }
      default:
        return {
          primary: "#8548A8",
          hover: "#7A4397",
        }
    }
  }

  const colors = getColorScheme(userType)

  const handleConfirmLogout = async () => {
    setIsLoggingOut(true)
    try {
      await onConfirm()
    } catch (error) {
      console.error('Logout confirmation error:', error)
    } finally {
      setIsLoggingOut(false)
    }
  }

  if (!isOpen) return null

  return (
    <>
      {/* Backdrop */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px'
        }}
        onClick={(e) => {
          if (e.target === e.currentTarget && !isLoggingOut) {
            onClose()
          }
        }}
      >
        {/* Modal */}
        <div
          style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            padding: '32px',
            maxWidth: '400px',
            width: '100%',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)',
            fontFamily: '"Red Hat Display", sans-serif',
            textAlign: 'center'
          }}
          dir={modalDirection}
          lang={i18n?.language}
        >
          {/* Icon */}
          <div
            style={{
              width: '64px',
              height: '64px',
              borderRadius: '50%',
              backgroundColor: `${colors.primary}15`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 24px'
            }}
          >
            <LogOut size={28} color={colors.primary} />
          </div>

          {/* Title */}
          <h3
            style={{
              fontSize: '24px',
              fontWeight: '600',
              color: '#1F2937',
              margin: '0 0 12px 0',
              lineHeight: '1.3'
            }}
          >
            {title}
          </h3>

          {/* Description */}
          <p
            style={{
              fontSize: '16px',
              color: '#6B7280',
              margin: subtitle ? '0 0 8px 0' : '0 0 32px 0',
              lineHeight: '1.5'
            }}
          >
            {question}
          </p>
          {subtitle && (
            <p
              style={{
                fontSize: '14px',
                color: '#9CA3AF',
                margin: '0 0 32px 0',
                lineHeight: '1.5'
              }}
            >
              {subtitle}
            </p>
          )}

          {/* Buttons */}
          <div
            style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'center'
            }}
          >
            {/* Cancel Button */}
            <button
              onClick={onClose}
              disabled={isLoggingOut}
              style={{
                flex: 1,
                height: '48px',
                backgroundColor: 'transparent',
                border: '1.5px solid #E5E7EB',
                borderRadius: '12px',
                fontSize: '16px',
                fontWeight: '500',
                color: '#6B7280',
                cursor: isLoggingOut ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease',
                fontFamily: '"Red Hat Display", sans-serif',
                opacity: isLoggingOut ? 0.5 : 1
              }}
              onMouseEnter={(e) => {
                if (!isLoggingOut) {
                  e.target.style.borderColor = '#D1D5DB'
                  e.target.style.backgroundColor = '#F9FAFB'
                }
              }}
              onMouseLeave={(e) => {
                if (!isLoggingOut) {
                  e.target.style.borderColor = '#E5E7EB'
                  e.target.style.backgroundColor = 'transparent'
                }
              }}
            >
              {cancelText}
            </button>

            {/* Logout Button */}
            <button
              onClick={handleConfirmLogout}
              disabled={isLoggingOut}
              style={{
                flex: 1,
                height: '48px',
                backgroundColor: colors.primary,
                border: 'none',
                borderRadius: '12px',
                fontSize: '16px',
                fontWeight: '600',
                color: 'white',
                cursor: isLoggingOut ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease',
                fontFamily: '"Red Hat Display", sans-serif',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
              onMouseEnter={(e) => {
                if (!isLoggingOut) {
                  e.target.style.backgroundColor = colors.hover
                  e.target.style.transform = 'translateY(-1px)'
                }
              }}
              onMouseLeave={(e) => {
                if (!isLoggingOut) {
                  e.target.style.backgroundColor = colors.primary
                  e.target.style.transform = 'translateY(0)'
                }
              }}
            >
              {isLoggingOut && <Loader2 size={16} className="animate-spin" />}
              {isLoggingOut ? loggingOutText : confirmText}
            </button>
          </div>
        </div>
      </div>

      {/* Animation styles */}
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }

          .animate-spin {
            animation: spin 1s linear infinite;
          }
        `}
      </style>
    </>
  )
}

export default LogoutConfirmationModal